import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { commonStyles, colors, typography, spacing } from '../styles/design-system';
import apiClient from '../api/client';

interface AIModel {
  model_id: number;
  provider: string;
  model_code: string;
  model_name: string;
  description: string | null;
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string | null;
  updated_at: string | null;
}

const AIModelManagement: React.FC = () => {
  const [groupedModels, setGroupedModels] = useState<Record<string, AIModel[]>>({});
  const [loading, setLoading] = useState(false);

  // 모달 상태
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingModel, setEditingModel] = useState<AIModel | null>(null);

  // 새 모델 폼 상태
  const [newModel, setNewModel] = useState({
    provider: 'gemini',
    model_code: '',
    model_name: '',
    description: '',
    is_default: false,
    sort_order: 0
  });

  const providerInfo: Record<string, { label: string; description: string }> = {
    gemini: {
      label: 'Google Gemini',
      description: '멀티모달 이해, 추론, 코딩에 뛰어난 Google AI 모델'
    },
    claude: {
      label: 'Anthropic Claude',
      description: '안전하고 윤리적인 AI, 코딩 및 에이전트 작업에 최적화'
    },
    openai: {
      label: 'OpenAI',
      description: '시각적 추론, 멀티모달 처리에 뛰어난 GPT 시리즈'
    }
  };

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/ai/models', {
        params: { include_inactive: true }
      });
      setGroupedModels(response.data.grouped || {});
    } catch (error: any) {
      console.error('AI 모델 조회 실패:', error);
      toast.error('AI 모델 목록을 불러오는데 실패했음.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (model: AIModel) => {
    try {
      await apiClient.post(`/api/ai/models/${model.model_id}/set-default`);
      toast.success(`'${model.model_name}'이(가) 기본 모델로 설정되었음.`);
      setShowSelectModal(false);
      loadModels();
    } catch (error: any) {
      console.error('기본 모델 설정 실패:', error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message || error.message;
      toast.error(`기본 모델 설정 실패: ${message}`);
    }
  };

  const handleCreateModel = async () => {
    if (!newModel.model_code || !newModel.model_name) {
      toast.error('모델 코드와 이름을 입력해.');
      return;
    }

    try {
      await apiClient.post('/api/ai/models', newModel);
      toast.success('모델이 추가되었음.');
      setShowAddModal(false);
      setNewModel({
        provider: 'gemini',
        model_code: '',
        model_name: '',
        description: '',
        is_default: false,
        sort_order: 0
      });
      loadModels();
    } catch (error: any) {
      console.error('모델 생성 실패:', error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message || error.message;
      toast.error(`모델 추가 실패: ${message}`);
    }
  };

  const handleUpdateModel = async () => {
    if (!editingModel) return;

    try {
      await apiClient.put(`/api/ai/models/${editingModel.model_id}`, {
        model_name: editingModel.model_name,
        description: editingModel.description,
        is_active: editingModel.is_active,
        sort_order: editingModel.sort_order
      });
      toast.success('모델이 수정되었음.');
      setShowEditModal(false);
      setEditingModel(null);
      loadModels();
    } catch (error: any) {
      console.error('모델 업데이트 실패:', error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message || error.message;
      toast.error(`모델 수정 실패: ${message}`);
    }
  };

  const openSelectModal = (provider: string) => {
    setSelectedProvider(provider);
    setShowSelectModal(true);
  };

  const getDefaultModel = (provider: string): AIModel | undefined => {
    return groupedModels[provider]?.find(m => m.is_default && m.is_active);
  };

  const getActiveModelCount = (provider: string): number => {
    return groupedModels[provider]?.filter(m => m.is_active).length || 0;
  };

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <h3 style={styles.sectionTitle}>AI 모델 관리</h3>
        <div style={styles.headerActions}>
          <button
            onClick={() => setShowAddModal(true)}
            style={styles.primaryButton}
          >
            모델 추가
          </button>
        </div>
      </div>

      {/* 메인 테이블 */}
      {loading ? (
        <div style={styles.loading}>로딩 중...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Provider</th>
                <th style={styles.th}>현재 모델 코드</th>
                <th style={styles.th}>설명</th>
                <th style={styles.th}>등록</th>
              </tr>
            </thead>
            <tbody>
              {['gemini', 'claude', 'openai'].map(provider => {
                const defaultModel = getDefaultModel(provider);
                const activeCount = getActiveModelCount(provider);
                const info = providerInfo[provider];

                return (
                  <tr
                    key={provider}
                    onClick={() => openSelectModal(provider)}
                    style={styles.clickableRow}
                  >
                    <td style={styles.td}>
                      <div style={styles.providerCell}>
                        <span style={styles.providerName}>{info.label}</span>
                        <span style={styles.providerDesc}>{info.description}</span>
                      </div>
                    </td>
                    <td style={styles.td}>
                      {defaultModel ? (
                        <div style={styles.modelCodeCell}>
                          <span style={styles.modelName}>{defaultModel.model_name}</span>
                          <code style={styles.code}>{defaultModel.model_code}</code>
                        </div>
                      ) : (
                        <span style={styles.noModel}>미설정</span>
                      )}
                    </td>
                    <td style={styles.tdDescription}>
                      {defaultModel?.description || '-'}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.countText}>{activeCount}개</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 모델 선택 모달 */}
      {showSelectModal && selectedProvider && (
        <div style={styles.modalBackdrop} onClick={() => setShowSelectModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                {providerInfo[selectedProvider].label} 모델 선택
              </h3>
              <button onClick={() => setShowSelectModal(false)} style={styles.closeButton}>×</button>
            </div>

            <div style={styles.modelList}>
              {(groupedModels[selectedProvider] || []).map(model => (
                <div
                  key={model.model_id}
                  style={{
                    ...styles.modelItem,
                    ...(model.is_default ? styles.modelItemSelected : {}),
                    ...(!model.is_active ? styles.modelItemInactive : {})
                  }}
                >
                  <div style={styles.modelItemContent}>
                    <div style={styles.modelItemHeader}>
                      <span style={styles.modelItemName}>{model.model_name}</span>
                      {model.is_default && <span style={styles.currentBadge}>사용중</span>}
                      {!model.is_active && <span style={styles.inactiveBadge}>비활성</span>}
                    </div>
                    <code style={styles.modelItemCode}>{model.model_code}</code>
                    {model.description && (
                      <p style={styles.modelItemDesc}>{model.description}</p>
                    )}
                  </div>
                  <div style={styles.modelItemActions}>
                    {model.is_active && !model.is_default && (
                      <button
                        onClick={() => handleSetDefault(model)}
                        style={styles.smallPrimaryButton}
                      >
                        선택
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setEditingModel(model);
                        setShowSelectModal(false);
                        setShowEditModal(true);
                      }}
                      style={styles.smallSecondaryButton}
                    >
                      수정
                    </button>
                  </div>
                </div>
              ))}

              {(groupedModels[selectedProvider] || []).length === 0 && (
                <div style={styles.emptyState}>등록된 모델이 없음.</div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button
                onClick={() => {
                  setNewModel({ ...newModel, provider: selectedProvider });
                  setShowSelectModal(false);
                  setShowAddModal(true);
                }}
                style={styles.secondaryButton}
              >
                + 새 모델 추가
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 모델 추가 모달 */}
      {showAddModal && (
        <div style={styles.modalBackdrop} onClick={() => setShowAddModal(false)}>
          <div style={styles.modalContentSmall} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>새 모델 추가</h3>
              <button onClick={() => setShowAddModal(false)} style={styles.closeButton}>×</button>
            </div>

            <div style={styles.formBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Provider</label>
                <select
                  value={newModel.provider}
                  onChange={(e) => setNewModel({ ...newModel, provider: e.target.value })}
                  style={styles.select}
                >
                  <option value="gemini">Gemini</option>
                  <option value="claude">Claude</option>
                  <option value="openai">OpenAI</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>모델 코드 (API용) *</label>
                <input
                  type="text"
                  value={newModel.model_code}
                  onChange={(e) => setNewModel({ ...newModel, model_code: e.target.value })}
                  placeholder="예: gemini-2.5-flash"
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>모델 이름 (표시용) *</label>
                <input
                  type="text"
                  value={newModel.model_name}
                  onChange={(e) => setNewModel({ ...newModel, model_name: e.target.value })}
                  placeholder="예: Gemini 2.5 Flash"
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>설명</label>
                <textarea
                  value={newModel.description}
                  onChange={(e) => setNewModel({ ...newModel, description: e.target.value })}
                  placeholder="모델에 대한 간단한 설명"
                  style={styles.textarea}
                  rows={3}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>정렬 순서</label>
                <input
                  type="number"
                  value={newModel.sort_order}
                  onChange={(e) => setNewModel({ ...newModel, sort_order: parseInt(e.target.value) || 0 })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={newModel.is_default}
                    onChange={(e) => setNewModel({ ...newModel, is_default: e.target.checked })}
                  />
                  기본 모델로 설정
                </label>
              </div>
            </div>

            <div style={styles.modalActions}>
              <button onClick={() => setShowAddModal(false)} style={styles.secondaryButton}>
                취소
              </button>
              <button onClick={handleCreateModel} style={styles.primaryButton}>
                추가
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 모델 수정 모달 */}
      {showEditModal && editingModel && (
        <div style={styles.modalBackdrop} onClick={() => setShowEditModal(false)}>
          <div style={styles.modalContentSmall} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>모델 수정</h3>
              <button onClick={() => setShowEditModal(false)} style={styles.closeButton}>×</button>
            </div>

            <div style={styles.formBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>모델 코드</label>
                <code style={styles.codeBlock}>{editingModel.model_code}</code>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>모델 이름</label>
                <input
                  type="text"
                  value={editingModel.model_name}
                  onChange={(e) => setEditingModel({ ...editingModel, model_name: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>설명</label>
                <textarea
                  value={editingModel.description || ''}
                  onChange={(e) => setEditingModel({ ...editingModel, description: e.target.value })}
                  style={styles.textarea}
                  rows={3}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>정렬 순서</label>
                <input
                  type="number"
                  value={editingModel.sort_order}
                  onChange={(e) => setEditingModel({ ...editingModel, sort_order: parseInt(e.target.value) || 0 })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={editingModel.is_active}
                    onChange={(e) => setEditingModel({ ...editingModel, is_active: e.target.checked })}
                  />
                  활성화
                </label>
              </div>
            </div>

            <div style={styles.modalActions}>
              <button onClick={() => setShowEditModal(false)} style={styles.secondaryButton}>
                취소
              </button>
              <button onClick={handleUpdateModel} style={styles.primaryButton}>
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  // 컨테이너
  container: {
    backgroundColor: colors.gray[50],
    padding: spacing[5],
    border: `1px solid ${colors.gray[200]}`,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing[5],
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[3],
  },
  sectionTitle: {
    ...commonStyles.sectionTitle,
    margin: 0,
  },
  loading: commonStyles.loadingState,
  emptyState: commonStyles.emptyState,

  // 테이블
  tableContainer: {
    ...commonStyles.tableContainer,
    minWidth: '800px',
  },
  table: commonStyles.table,
  th: {
    ...commonStyles.th,
    whiteSpace: 'nowrap',
  },
  td: {
    ...commonStyles.td,
    verticalAlign: 'top',
    paddingTop: spacing[4],
    paddingBottom: spacing[4],
  },
  clickableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  tdDescription: {
    ...commonStyles.td,
    verticalAlign: 'top',
    paddingTop: spacing[4],
    paddingBottom: spacing[4],
    maxWidth: '250px',
    whiteSpace: 'normal',
    lineHeight: typography.lineHeight.normal,
    fontSize: typography.fontSize.sm,
    color: colors.gray[600],
  },

  // Provider 셀
  providerCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[1],
  },
  providerName: {
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
    fontSize: typography.fontSize.md,
  },
  providerDesc: {
    fontSize: typography.fontSize.xs,
    color: colors.gray[500],
    maxWidth: '180px',
    lineHeight: typography.lineHeight.normal,
    whiteSpace: 'normal',
  },

  noModel: {
    color: colors.gray[400],
    fontStyle: 'italic',
  },
  countText: {
    color: colors.gray[600],
  },
  modelCodeCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[1],
  },
  modelName: {
    fontWeight: typography.fontWeight.medium,
    color: colors.black,
    fontSize: typography.fontSize.base,
  },

  // 코드
  code: {
    backgroundColor: colors.gray[100],
    padding: `${spacing[1]} ${spacing[2]}`,
    fontFamily: typography.fontFamilyMono,
    fontSize: typography.fontSize.sm,
    color: colors.gray[700],
  },
  codeBlock: {
    display: 'block',
    backgroundColor: colors.gray[100],
    padding: spacing[3],
    fontFamily: typography.fontFamilyMono,
    fontSize: typography.fontSize.sm,
    color: colors.gray[700],
  },

  // 버튼
  primaryButton: {
    ...commonStyles.button,
    ...commonStyles.buttonPrimary,
  },
  secondaryButton: {
    ...commonStyles.button,
    ...commonStyles.buttonSecondary,
  },
  smallPrimaryButton: {
    ...commonStyles.button,
    ...commonStyles.buttonPrimary,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
  },
  smallSecondaryButton: {
    ...commonStyles.button,
    ...commonStyles.buttonSecondary,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
  },

  // 모달
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: colors.white,
    width: '550px',
    maxWidth: '90vw',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${colors.gray[200]}`,
  },
  modalContentSmall: {
    backgroundColor: colors.white,
    width: '420px',
    maxWidth: '90vw',
    border: `1px solid ${colors.gray[200]}`,
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${spacing[4]} ${spacing[5]}`,
    borderBottom: `1px solid ${colors.gray[200]}`,
    backgroundColor: colors.gray[50],
  },
  modalTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    margin: 0,
    color: colors.black,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: colors.gray[500],
    padding: 0,
    lineHeight: 1,
  },
  modalFooter: {
    padding: spacing[4],
    borderTop: `1px solid ${colors.gray[200]}`,
    backgroundColor: colors.gray[50],
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: spacing[3],
    padding: spacing[5],
    borderTop: `1px solid ${colors.gray[200]}`,
    backgroundColor: colors.gray[50],
  },

  // 모델 리스트
  modelList: {
    padding: spacing[4],
    overflowY: 'auto',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[3],
  },
  modelItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: spacing[4],
    backgroundColor: colors.white,
    border: `1px solid ${colors.gray[200]}`,
  },
  modelItemSelected: {
    backgroundColor: colors.gray[100],
    borderColor: colors.black,
  },
  modelItemInactive: {
    opacity: 0.5,
  },
  modelItemContent: {
    flex: 1,
    marginRight: spacing[4],
  },
  modelItemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[3],
    marginBottom: spacing[2],
  },
  modelItemName: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
  },
  currentBadge: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
    color: colors.white,
    backgroundColor: colors.black,
    padding: `${spacing[1]} ${spacing[2]}`,
  },
  inactiveBadge: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
    color: colors.status.error,
    backgroundColor: colors.gray[100],
    padding: `${spacing[1]} ${spacing[2]}`,
  },
  modelItemCode: {
    display: 'block',
    fontSize: typography.fontSize.sm,
    color: colors.gray[600],
    fontFamily: typography.fontFamilyMono,
    marginBottom: spacing[2],
  },
  modelItemDesc: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[500],
    margin: 0,
    lineHeight: typography.lineHeight.normal,
  },
  modelItemActions: {
    display: 'flex',
    gap: spacing[2],
    flexShrink: 0,
  },

  // 폼
  formBody: {
    padding: spacing[5],
  },
  formGroup: {
    ...commonStyles.formGroupVertical,
    marginBottom: spacing[4],
  },
  label: {
    ...commonStyles.label,
    minWidth: 'auto',
  },
  input: {
    ...commonStyles.input,
    width: '100%',
    boxSizing: 'border-box',
  },
  select: {
    ...commonStyles.select,
    width: '100%',
  },
  textarea: {
    ...commonStyles.textarea,
    boxSizing: 'border-box',
  },
  checkboxLabel: {
    ...commonStyles.checkboxLabel,
  },
};

export default AIModelManagement;
